import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, CheckCircle2, Send, DoorOpen } from "lucide-react";
import VideoPlayer from "../components/VideoPlayer";

export default function Lesson() {
  const { pillarId, lessonId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [journal, setJournal] = useState("");
  const [savedNote, setSavedNote] = useState(false);
  const [messages, setMessages] = useState([]);
  const [roomInput, setRoomInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [completing, setCompleting] = useState(false);
  const chatEndRef = useRef(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    setData(null); setError(null);
    api.get(`/course/lesson/${pillarId}/${lessonId}`).then((r) => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 402) setError("paywall");
        else setError(e.response?.data?.detail || "Could not load lesson");
      });
    api.get(`/coach/history/${pillarId}/${lessonId}`).then((r) => setMessages(r.data.messages)).catch(() => {});
  }, [pillarId, lessonId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const saveJournal = async () => {
    if (!journal.trim()) return;
    await api.post("/journal", { pillar_id: pillarId, lesson_id: lessonId, content: journal });
    setJournal("");
    setSavedNote(true);
    setTimeout(() => setSavedNote(false), 2500);
  };

  const completeLesson = async () => {
    setCompleting(true);
    try {
      await api.post("/course/complete-lesson", { pillar_id: pillarId, lesson_id: lessonId });
      setData({ ...data, completed: true });
    } finally { setCompleting(false); }
  };

  const sendMessage = async () => {
    if (!roomInput.trim() || streaming) return;
    const msg = roomInput.trim();
    setRoomInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const token = localStorage.getItem("da_token");
      const resp = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pillar_id: pillarId, lesson_id: lessonId, message: msg }),
      });
      if (!resp.body) throw new Error("No stream");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = {
              role: "assistant",
              content: (copy[copy.length - 1].content || "") + payload,
            };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "(The room is quiet right now. Please try again.)" };
        return copy;
      });
    } finally { setStreaming(false); }
  };

  if (error === "paywall") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <div className="font-display text-4xl text-[#2D2A26]">This lesson is part of the full course.</div>
        <p className="text-[#5C5852] mt-5 leading-relaxed">
          You've completed the free preview. Unlock all 9 lessons, your reflection journal, and The Inner Room with a one-time enrollment.
        </p>
        <Link to="/pricing" data-testid="lesson-paywall-cta" className="btn-primary rounded-full px-8 py-4 mt-10 inline-block font-medium">
          Unlock the full course
        </Link>
      </div>
    );
  }

  if (!data) return <div className="py-32 text-center text-[#8A857D]">{error || "Loading..."}</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 py-12">
      <button onClick={() => nav("/dashboard")} className="flex items-center gap-2 text-sm text-[#5C5852] hover:text-[#2D2A26] mb-8" data-testid="lesson-back">
        <ArrowLeft size={16} /> Back to dashboard
      </button>

      <div className="text-xs uppercase tracking-[0.25em] text-[#A36B5E]" data-testid="lesson-pillar">{data.pillar.title}</div>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2D2A26] mt-3 tracking-tight" data-testid="lesson-title">
        {data.lesson.title}
      </h1>
      <div className="text-sm text-[#8A857D] mt-3">{data.lesson.duration_min} min · {data.lesson.scripture_focus}</div>

      {/* Video */}
      {data.lesson.video_url && (
        <div className="mt-10">
          <VideoPlayer url={data.lesson.video_url} title={data.lesson.title} />
        </div>
      )}

      {/* Scripture */}
      <blockquote className="mt-10 border-l-2 border-[#A36B5E] pl-6 italic text-[#2D2A26] text-lg leading-relaxed">
        "{data.pillar.scripture_text}"
        <div className="not-italic text-xs uppercase tracking-widest text-[#8A857D] mt-3">— {data.pillar.scripture_ref}</div>
      </blockquote>

      {/* Lesson body */}
      <div className="mt-10 space-y-6 text-[#2D2A26] leading-relaxed text-lg" data-testid="lesson-body">
        {data.lesson.body.map((para, i) => (<p key={i}>{para}</p>))}
      </div>

      {/* Reflection prompt */}
      <div className="mt-12 bg-[#F5F2EB] rounded-2xl p-8">
        <div className="text-xs uppercase tracking-widest text-[#5F6F52]">Reflection</div>
        <div className="font-display text-2xl text-[#2D2A26] mt-2">{data.lesson.reflection_prompt}</div>
        <div className="mt-6">
          <textarea
            value={journal}
            onChange={(e) => setJournal(e.target.value)}
            rows={5}
            placeholder="Begin journaling here..."
            data-testid="lesson-journal-input"
            className="w-full px-4 py-3 rounded-xl border border-[#EAE5D9] bg-white text-[#2D2A26] focus:outline-none focus:border-[#A36B5E] resize-none"
          />
          <div className="flex justify-between items-center mt-3">
            <div className="text-xs text-[#5F6F52]">{savedNote && "Saved to your journal ✓"}</div>
            <button onClick={saveJournal} disabled={!journal.trim()} data-testid="lesson-journal-save" className="btn-outline-clay rounded-full px-5 py-2 text-sm disabled:opacity-50">
              Save reflection
            </button>
          </div>
        </div>
      </div>

      {/* Action step */}
      <div className="mt-8 bg-white border border-[#C49A7E]/30 rounded-2xl p-6">
        <div className="text-xs uppercase tracking-widest text-[#A36B5E]">Action Step</div>
        <div className="mt-2 text-[#2D2A26]">{data.lesson.action_step}</div>
      </div>

      {/* The Inner Room */}
      <div className="mt-12 bg-white border border-[#EAE5D9] rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-[#EAE5D9] flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F5F2EB] flex items-center justify-center">
            <DoorOpen size={18} className="text-[#A36B5E]" />
          </div>
          <div>
            <div className="font-display text-xl text-[#2D2A26]">The Inner Room</div>
            <div className="text-xs text-[#8A857D]">A quiet place to think it through — anchored in this lesson's Scripture.</div>
          </div>
        </div>
        <div className="px-6 py-4 max-h-[400px] overflow-y-auto space-y-4" data-testid="inner-room-messages">
          {messages.length === 0 && (
            <div className="text-sm text-[#8A857D] italic py-6 text-center">
              Take a seat. Say what's stirring after the reflection prompt. The room listens, responds with Scripture, and asks back.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                m.role === "user" ? "bg-[#A36B5E] text-white" : "bg-[#F5F2EB] text-[#2D2A26]"
              }`}>
                <div className="text-sm whitespace-pre-wrap leading-relaxed">
                  {m.content || (streaming && i === messages.length - 1 ? (
                    <span><span className="typing-dot"/><span className="typing-dot"/><span className="typing-dot"/></span>
                  ) : null)}
                </div>
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <div className="p-4 border-t border-[#EAE5D9] flex gap-2">
          <input
            value={roomInput}
            onChange={(e) => setRoomInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
            placeholder="What's on your heart right now?"
            disabled={streaming}
            data-testid="inner-room-input"
            className="flex-1 px-4 py-3 rounded-full border border-[#EAE5D9] bg-[#FDFBF7] focus:outline-none focus:border-[#A36B5E]"
          />
          <button onClick={sendMessage} disabled={streaming || !roomInput.trim()} data-testid="inner-room-send" className="btn-primary rounded-full px-5 disabled:opacity-50">
            <Send size={16} />
          </button>
        </div>
        <div className="px-6 pb-4 -mt-1 text-[11px] text-[#8A857D] text-center italic">
          Responses are thoughtfully generated — a quiet sounding board, not a substitute for prayer, pastoral counsel, or community.
        </div>
      </div>

      {/* Complete */}
      <div className="mt-10 flex justify-between items-center">
        <Link to="/dashboard" className="text-sm text-[#5C5852]">← Back to dashboard</Link>
        {data.completed ? (
          <div className="inline-flex items-center gap-2 text-[#5F6F52] text-sm" data-testid="lesson-completed">
            <CheckCircle2 size={18} /> Lesson complete
          </div>
        ) : (
          <button onClick={completeLesson} disabled={completing} data-testid="lesson-complete-btn" className="btn-primary rounded-full px-8 py-3 font-medium disabled:opacity-50">
            {completing ? "Saving..." : "Mark as complete"}
          </button>
        )}
      </div>
    </div>
  );
}
